/**
 * Handler errors: a FIXED code plus a FIXED message, never a relayed
 * exception.
 *
 * A database driver that cannot connect says so with the connection string in
 * hand, password included; a store error can carry a path or a record id. Both
 * would otherwise land in a model's context by way of an error string nobody
 * examined. So the JS side names the failure and Rust frames it — there is one
 * vocabulary, written here, and `mcp::dispatch` turns each entry into a
 * tool-execution error (`isError: true`), which is what the spec reserves that
 * mechanism for.
 */
export function mcpError(code, message, extra = {}) {
  return { error: code, message, ...extra };
}

/** The full set, so a test can assert nothing else is ever emitted. */
export const ERROR_CODES = Object.freeze([
  'not_found',
  'unavailable',
  'conflict',
  'expected_rev_required',
  'document_too_large',
  // Phase 2 (boards write). `record_too_large` is deliberately NOT a reuse of
  // `document_too_large`: a task is not a document, and a model that reads
  // "document_too_large" after `board_task_create` will go looking for a
  // document it never wrote.
  'record_too_large',
  // The named id exists but is not where the arguments claim: a list that
  // belongs to another board, a board id that names a list, a move into a list
  // on a different board. Distinct from `not_found` because the model's
  // correction is different — re-read the board, don't create the thing.
  'invalid_parent',
  // An argument the schema cannot express: a label id that is not in the
  // board's own palette, a position past the end of a list, a due date that is
  // not a date.
  'invalid_argument',
  // Phase 2 (deletion). The call names something that is not a single node, and
  // `cascade: true` was not supplied. This is NOT a request for the USER to
  // confirm — the destructive switch is that, and it is already on by the time
  // a delete tool is even visible. It is an argument the MODEL must supply, and
  // it turns "one wrong id deletes a 200-node tree, permanently, with no undo"
  // into "one wrong id returns a listing of what would be deleted". The refusal
  // carries that listing, so the second call is informed rather than blind.
  'cascade_required',
  // Card 5258c71d. The request exists and the argument is well formed, but the
  // request's KIND does not use that field — `headers` on a gRPC request,
  // `body` on a WebSocket one. Distinct from `invalid_argument` because the
  // model's correction is different: the same call on an HTTP request is fine.
  'unsupported_for_type',
  // Plans v2 (16.10.2026): the account's plan caps what it may create on this
  // machine (Free: 1 board, 25 tasks, 5 documents …). The message names the
  // count, the cap and the plan that lifts it; existing items are untouched.
  'plan_limit',
  'write_failed',
  'unknown_tool',
]);
